/**
 * Custom element for the table of contents functionality
 * Handles smooth scrolling to sections and active state management
 */
class TableOfContents extends HTMLElement {
  constructor() {
    super();
    this.links = this.querySelectorAll('.table-of-contents__link');
    this.init();
  }

  init() {
    this.addClickListeners();
    this.addScrollListener();
  }

  /**
   * Add click listeners for smooth scrolling
   */
  addClickListeners() {
    this.links.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = link.getAttribute('href').substring(1);
        const targetElement = document.getElementById(targetId);
        
        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }

  /**
   * Scroll the table of contents to keep the active link visible
   */
  scrollToActiveLink(activeLink) {
    const tocList = this.querySelector('.table-of-contents__list');
    if (!tocList || !activeLink) return;
    
    const tocContainer = tocList;
    const linkRect = activeLink.getBoundingClientRect();
    const containerRect = tocContainer.getBoundingClientRect();
    
    // Calculate if the link is outside the visible area
    const linkTop = linkRect.top - containerRect.top;
    const linkBottom = linkRect.bottom - containerRect.top;
    const containerHeight = containerRect.height;
    
    // Add some padding for better UX
    const padding = 40;
    
    // Check if link is above the visible area
    if (linkTop < padding) {
      const scrollTop = tocContainer.scrollTop + linkTop - padding;
      tocContainer.scrollTo({
        top: scrollTop,
        behavior: 'smooth'
      });
    }
    // Check if link is below the visible area
    else if (linkBottom > containerHeight - padding) {
      const scrollTop = tocContainer.scrollTop + linkBottom - containerHeight + padding;
      tocContainer.scrollTo({
        top: scrollTop,
        behavior: 'smooth'
      });
    }
  }

  /**
   * Add scroll listener to highlight active section (mobile only)
   */
  addScrollListener() {
    // Only enable active states on mobile
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    
    if (!isMobile) {
      return; // Skip active state tracking on desktop
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const id = entry.target.id;
          const activeLink = this.querySelector(`[href="#${id}"]`);
          
          // Remove active class from all links
          this.links.forEach(link => link.classList.remove('active'));
          
          // Add active class to current link
          if (activeLink) {
            activeLink.classList.add('active');
            
            // Scroll the table of contents to keep active link visible
            this.scrollToActiveLink(activeLink);
          }
        }
      });
    }, {
      rootMargin: '-20% 0px -70% 0px'
    });

    // Observe all letter sections
    document.querySelectorAll('.collections-list__group').forEach(group => {
      observer.observe(group);
    });
    
    // Re-initialize observer when screen size changes
    window.matchMedia('(max-width: 768px)').addEventListener('change', (e) => {
      if (e.matches) {
        // Switched to mobile - start observing
        this.addScrollListener();
      } else {
        // Switched to desktop - remove all active states
        this.links.forEach(link => link.classList.remove('active'));
      }
    });
  }
}

/**
 * Custom element for the collections list
 * Handles any additional functionality for the collections display
 */
class CollectionsList extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.addLinkTracking();
  }

  /**
   * Add analytics or tracking for collection link clicks
   */
  addLinkTracking() {
    const links = this.querySelectorAll('.collections-list__link');
    
    links.forEach(link => {
      link.addEventListener('click', () => {
        // Add any tracking or analytics here
        const collectionName = link.textContent.trim();
        console.log(`Collection clicked: ${collectionName}`);
        
        // Example: Send tracking event
        // if (typeof gtag !== 'undefined') {
        //   gtag('event', 'collection_click', {
        //     collection_name: collectionName
        //   });
        // }
      });
    });
  }
}

/**
 * Header height detection for sticky positioning
 * Uses multiple observers to accurately track the header's effective position
 */
let headerHeight = 0;
let isHeaderVisible = true;

function updateHeaderHeight() {
  const header = document.querySelector('sticky-header');
  
  if (!header) {
    document.documentElement.style.setProperty('--header-height', '0px');
    document.documentElement.style.setProperty('--header-offset', '0px');
    return;
  }

  // Get the header's position and size
  const headerRect = header.getBoundingClientRect();
  const computedStyle = getComputedStyle(header);
  const transform = computedStyle.transform;
  
  // Get the header's natural height
  headerHeight = header.offsetHeight;
  
  let effectiveHeight = 0;
  let headerOffset = 0;
  
  // Calculate the header's effective position
  if (headerRect.top <= 0) {
    // Header is at or above the viewport top
    if (transform && transform !== 'none') {
      // Parse transform matrix to get translateY value
      const matrix = new DOMMatrixReadOnly(transform);
      const translateY = matrix.m42; // translateY value
      
      // Calculate visible height and offset based on transform
      if (translateY < 0) {
        // Header is being translated up (hiding)
        effectiveHeight = Math.max(0, headerHeight + translateY);
        headerOffset = Math.max(translateY, -headerHeight);
      } else {
        // Header is fully visible or translating down
        effectiveHeight = headerHeight;
        headerOffset = 0;
      }
    } else {
      // No transform, header is sticky at top
      effectiveHeight = Math.max(0, headerRect.bottom);
      headerOffset = 0;
    }
  } else {
    // Header is below viewport top (normal flow)
    effectiveHeight = headerHeight;
    headerOffset = 0;
  }
  
  // Update CSS variables
  document.documentElement.style.setProperty('--header-height', `${effectiveHeight}px`);
  document.documentElement.style.setProperty('--header-offset', `${headerOffset}px`);
}

// Intersection Observer to track header visibility
function setupHeaderObservers() {
  const header = document.querySelector('sticky-header');
  if (!header) return;
  
  // Intersection Observer for basic visibility
  const intersectionObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      isHeaderVisible = entry.isIntersecting;
      updateHeaderHeight();
    });
  }, {
    root: null,
    rootMargin: '0px',
    threshold: [0, 0.1, 0.5, 1]
  });
  
  intersectionObserver.observe(header);
  
  // Mutation Observer for style changes (transforms, etc.)
  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach(mutation => {
      if (mutation.type === 'attributes' && 
          (mutation.attributeName === 'style' || 
           mutation.attributeName === 'class')) {
        updateHeaderHeight();
      }
    });
  });
  
  mutationObserver.observe(header, {
    attributes: true,
    attributeFilter: ['style', 'class']
  });
  
  // Resize Observer for size changes
  const resizeObserver = new ResizeObserver(() => {
    updateHeaderHeight();
  });
  
  resizeObserver.observe(header);
  
  // RAF loop for smooth tracking during animations
  let rafId;
  let isTracking = false;
  
  function trackAnimations() {
    updateHeaderHeight();
    if (isTracking) {
      rafId = requestAnimationFrame(trackAnimations);
    }
  }
  
  function startTracking() {
    if (!isTracking) {
      isTracking = true;
      // Disable CSS transitions during JS updates for smoother tracking
      document.documentElement.style.setProperty('--disable-transitions', '1');
      trackAnimations();
    }
  }
  
  function stopTracking() {
    if (isTracking) {
      isTracking = false;
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      // Re-enable CSS transitions
      document.documentElement.style.removeProperty('--disable-transitions');
      updateHeaderHeight();
    }
  }
  
  // Start/stop animation tracking based on transitions
  header.addEventListener('transitionstart', startTracking);
  header.addEventListener('transitionend', stopTracking);
  
  // Also track during scroll when header is transitioning
  let scrollTimeout;
  window.addEventListener('scroll', () => {
    startTracking();
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(stopTracking, 150);
  });
}

// Initialize on DOM load
document.addEventListener('DOMContentLoaded', () => {
  setupHeaderObservers();
  updateHeaderHeight();
});

// Fallback scroll listener for edge cases
window.addEventListener('scroll', updateHeaderHeight);

/**
 * Custom element for scroll-to-top functionality
 * Shows/hides based on table of contents visibility on desktop
 */
class ScrollToTop extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    // Only enable on desktop
    if (window.matchMedia('(max-width: 768px)').matches) {
      return;
    }

    this.setupObserver();
    this.setupClickHandler();
    this.setupResizeHandler();
  }

  /**
   * Setup intersection observer to watch table of contents
   */
  setupObserver() {
    const tableOfContents = document.querySelector('.table-of-contents');
    
    if (!tableOfContents) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Table of contents is visible - hide button
          this.classList.remove('visible');
        } else {
          // Table of contents is not visible - show button
          this.classList.add('visible');
        }
      });
    }, {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    });

    observer.observe(tableOfContents);
  }

  /**
   * Setup click handler for smooth scroll to top
   */
  setupClickHandler() {
    this.addEventListener('click', () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    });
  }

  /**
   * Setup resize handler to enable/disable on screen size change
   */
  setupResizeHandler() {
    window.matchMedia('(max-width: 768px)').addEventListener('change', (e) => {
      if (e.matches) {
        // Switched to mobile - hide button
        this.classList.remove('visible');
      } else {
        // Switched to desktop - re-initialize
        this.init();
      }
    });
  }
}

// Register custom elements
customElements.define('table-of-contents', TableOfContents);
customElements.define('collections-list', CollectionsList);
customElements.define('scroll-to-top', ScrollToTop);
